/**
 * CAMEL Rating System Constants
 *
 * Based on official documentation from docs/visualization_guide/
 * Source: camel_weights.yaml and rating_scale_camel.yaml
 */

import scorecardRanges from '../data/scorecardRanges.json';

/**
 * Component Weights (must sum to 100%)
 * These represent the contribution of each component to the final CAMEL score
 */
export const COMPONENT_WEIGHTS: Record<string, number> = {
  C: 22.5596289,    // Capital / Solvency
  EG: 9.9506493,    // Efficiency & Earnings
  L: 29.3991792,    // Liquidity
  R: 7.9065909,     // Profitability (Rentabilidad)
  S: 10.5497997,    // Asset Quality (Calidad de Activos)
  STI: 19.6341519,  // Interest Rate Sensitivity (Sensibilidad Tasa Interes)
};

/**
 * Component Information
 */
export const COMPONENT_INFO: Record<string, {
  name: string;
  description: string;
  fullName: string;
  tooltip: string;
}> = {
  C: {
    name: 'Capital',
    fullName: 'Capital Adequacy & Solvency',
    description: 'Measures capital adequacy, leverage ratios, and ability to absorb losses',
    tooltip: 'Evaluates the institution\'s capital structure and ability to absorb losses. Includes capital ratios, leverage metrics, and solvency indicators. Weight: 22.56%',
  },
  EG: {
    name: 'Efficiency & Earnings',
    fullName: 'Operational Efficiency & Management',
    description: 'Evaluates operational efficiency, cost management, and earnings quality',
    tooltip: 'Measures how efficiently the institution manages its operations and generates earnings. Lower operating expense ratios indicate better efficiency. Weight: 9.95%',
  },
  L: {
    name: 'Liquidity',
    fullName: 'Liquidity Management',
    description: 'Assesses ability to meet short-term obligations and manage liquid assets',
    tooltip: 'Assesses the institution\'s ability to meet short-term obligations and maintain adequate liquid assets. Critical for financial stability. Weight: 29.40% (highest)',
  },
  R: {
    name: 'Profitability',
    fullName: 'Profitability & Returns',
    description: 'Measures return on assets, return on equity, and income generation',
    tooltip: 'Evaluates profitability through returns on assets (ROAA), returns on equity (ROAE), and various margin metrics. Weight: 7.91%',
  },
  S: {
    name: 'Asset Quality',
    fullName: 'Asset Quality & Credit Risk',
    description: 'Analyzes loan portfolio quality, default rates, and provision coverage',
    tooltip: 'Analyzes the quality of the loan portfolio, including default rates, non-performing loans, and provision coverage. Lower default rates indicate better asset quality. Weight: 10.55%',
  },
  STI: {
    name: 'Interest Rate Sensitivity',
    fullName: 'Market Risk & Interest Rate Sensitivity',
    description: 'Evaluates exposure to interest rate changes and market volatility',
    tooltip: 'Measures how sensitive the institution\'s financial margin and equity value are to interest rate changes. Higher sensitivity scores indicate better resilience. Weight: 19.64%',
  },
};

/**
 * Rating Scale (9-tier system)
 * Based on calculated CAMEL score (0-100)
 */
export interface RatingTier {
  grade: string;
  minScore: number;
  maxScore: number;
  riskLevel: 'MINIMAL' | 'LOW' | 'MODERATE' | 'ELEVATED' | 'HIGH' | 'VERY_HIGH' | 'EXTREME' | 'DEFAULT';
  description: string;
}

export const RATING_SCALE: RatingTier[] = [
  {
    grade: 'AAA',
    minScore: 88.90,
    maxScore: 100.00,
    riskLevel: 'MINIMAL',
    description: 'Exceptional financial strength with minimal credit risk',
  },
  {
    grade: 'AA',
    minScore: 77.79,
    maxScore: 88.89,
    riskLevel: 'LOW',
    description: 'Very strong financial position with low credit risk',
  },
  {
    grade: 'A',
    minScore: 66.68,
    maxScore: 77.78,
    riskLevel: 'LOW',
    description: 'Strong financial capacity with manageable risk',
  },
  {
    grade: 'BBB',
    minScore: 55.57,
    maxScore: 66.67,
    riskLevel: 'MODERATE',
    description: 'Adequate financial strength with moderate risk',
  },
  {
    grade: 'BB',
    minScore: 44.45,
    maxScore: 55.56,
    riskLevel: 'ELEVATED',
    description: 'Less than adequate financial capacity with elevated risk',
  },
  {
    grade: 'B',
    minScore: 33.34,
    maxScore: 44.44,
    riskLevel: 'HIGH',
    description: 'Weak financial position with high credit risk',
  },
  {
    grade: 'C',
    minScore: 22.23,
    maxScore: 33.33,
    riskLevel: 'VERY_HIGH',
    description: 'Very weak financial condition with very high risk',
  },
  {
    grade: 'D',
    minScore: 11.12,
    maxScore: 22.22,
    riskLevel: 'EXTREME',
    description: 'Critical financial condition with extreme risk',
  },
  {
    grade: 'E',
    minScore: 0.00,
    maxScore: 11.11,
    riskLevel: 'DEFAULT',
    description: 'In default or imminent default risk',
  },
];

/**
 * Performance Color Thresholds
 * Based on calculated score (0-100), NOT raw indicator values
 */
export const COLOR_THRESHOLDS = {
  STRONG: 66.68,    // A rating or better (Green)
  ADEQUATE: 44.45,  // BBB/BB rating (Yellow/Orange)
  // Below 44.45 is WEAK (Red)
};

/**
 * Performance Colors (consistent across all visualizations)
 * Re-exported from centralized colors.ts for backward compatibility.
 */
export { PERFORMANCE_COLORS } from './colors';

/**
 * Get performance color based on score
 * @param score - Calculated score (0-100)
 * @returns Hex color code
 */
export const getPerformanceColor = (score: number): string => {
  if (score >= COLOR_THRESHOLDS.STRONG) {
    return PERFORMANCE_COLORS.STRONG;
  }
  if (score >= COLOR_THRESHOLDS.ADEQUATE) {
    return PERFORMANCE_COLORS.ADEQUATE;
  }
  return PERFORMANCE_COLORS.WEAK;
};

/**
 * Get performance label based on score
 * @param score - Calculated score (0-100)
 * @returns Performance label
 */
export const getPerformanceLabel = (score: number): string => {
  if (score >= COLOR_THRESHOLDS.STRONG) {
    return 'Strong Performance';
  }
  if (score >= COLOR_THRESHOLDS.ADEQUATE) {
    return 'Adequate';
  }
  return 'Needs Attention';
};

/**
 * Get signal strength based on score
 * @param score - Calculated score (0-100)
 * @returns Signal strength level
 */
export const getSignalStrength = (score: number): 'strong' | 'moderate' | 'weak' => {
  if (score >= COLOR_THRESHOLDS.STRONG) {
    return 'strong';
  }
  if (score >= COLOR_THRESHOLDS.ADEQUATE) {
    return 'moderate';
  }
  return 'weak';
};

/**
 * Get rating grade from score
 * @param score - Calculated CAMEL score (0-100)
 * @returns Rating grade (AAA to E)
 */
export const getRatingGrade = (score: number): string => {
  const tier = RATING_SCALE.find(
    (tier) => score >= tier.minScore && score <= tier.maxScore
  );
  return tier?.grade || 'N/A';
};

/**
 * Get rating tier details from score
 * @param score - Calculated CAMEL score (0-100)
 * @returns Rating tier object or undefined
 */
export const getRatingTier = (score: number): RatingTier | undefined => {
  return RATING_SCALE.find(
    (tier) => score >= tier.minScore && score <= tier.maxScore
  );
};

/**
 * Normalize component score to 0-100 scale
 * @param score - Raw score from BigQuery (weighted contribution)
 * @param componentCode - Component code (C, EG, L, R, S, STI)
 * @returns Normalized score (0-100)
 */
export const normalizeComponentScore = (componentCode: string, score: number): number => {
  const weight = COMPONENT_WEIGHTS[componentCode];
  if (!weight) {
    console.warn(`Unknown component code: ${componentCode}`);
    return 0;
  }
  return Math.min(100, Math.max(0, (score / weight) * 100));
};

/**
 * Indicator Directionality
 * Defines whether higher or lower values indicate better performance
 */
export type IndicatorDirection = 'higher' | 'lower';

export const INDICATOR_DIRECTIONALITY: Record<string, IndicatorDirection> = {
  // Capital (C) - Higher is generally better
  C1: 'higher',  // Productive Assets / Total Assets
  C2: 'lower',   // Default Rate (Morosidad) - LOWER IS BETTER
  C3: 'higher',  // Intermediation Coefficient
  C4: 'higher',  // Provision Coverage

  // Efficiency & Earnings (EG) - Lower costs are better
  EG1: 'lower',  // Operating Expenses / Productive Assets - LOWER IS BETTER
  EG2: 'lower',  // Operating Expenses / Financial Margin - LOWER IS BETTER
  EG3: 'higher', // Operating Margin / Interest Paid

  // Liquidity (L) - Higher is better
  L1: 'higher',  // Liquid Investments Ratio
  L2: 'higher',  // Liquidity Coverage Ratio
  L3: 'higher',  // Available Funds / Demand Obligations

  // Profitability (R) - Higher is better
  R1: 'higher',  // ROAA
  R2: 'higher',  // ROAE
  R3: 'higher',  // Financial Income / Productive Assets
  R4: 'higher',  // Operating Margin / Financial Income
  R5: 'higher',  // Operating Margin / Gross Portfolio
  R6: 'higher',  // Operating Margin / Investments

  // Asset Quality (S) - Mixed directionality
  S1: 'higher',  // Global Capital Ratio
  S2: 'lower',   // Total Liabilities / (Capital + Reserves) - LOWER IS BETTER
  S3: 'lower',   // Total Liabilities / Total Assets - LOWER IS BETTER
  S4: 'lower',   // Gross Portfolio / Equity - LOWER IS BETTER
  S5: 'lower',   // Non-Performing Portfolio / Equity - LOWER IS BETTER
  S6: 'lower',   // Past Due Portfolio / Equity - LOWER IS BETTER
  S7: 'higher',  // Capitalization Index

  // Interest Rate Sensitivity (STI) - Higher resilience is better
  STI1: 'higher', // Financial Margin Sensitivity
  STI2: 'higher', // Equity Value Sensitivity
  ST1: 'higher',  // Alias for STI1 (data uses ST1)
  ST2: 'higher',  // Alias for STI2 (data uses ST2)
};

/**
 * Get directionality icon
 * @param indicatorId - Indicator ID
 * @returns Arrow icon (↑ or ↓)
 */
export const getDirectionalityIcon = (indicatorId: string): string => {
  const direction = INDICATOR_DIRECTIONALITY[indicatorId];
  return direction === 'higher' ? '↑' : '↓';
};

/**
 * Get directionality label
 * @param indicatorId - Indicator ID
 * @returns Label describing directionality
 */
export const getDirectionalityLabel = (indicatorId: string): string => {
  const direction = INDICATOR_DIRECTIONALITY[indicatorId];
  return direction === 'higher' ? 'Higher is Better' : 'Lower is Better';
};

/**
 * Calculate indicator score using official scorecard interpolation
 * Uses data from BigQuery indicator_scorecard_points table
 *
 * @param indicatorId - Indicator ID (e.g., 'S5', 'C2', 'L1')
 * @param value - Raw indicator value
 * @returns Calculated score (0-100) or null if no scorecard found
 */
export const calculateIndicatorScore = (indicatorId: string, value: number): number | null => {
  // Map ST1/ST2 to STI1/STI2 for scorecard lookup (data uses ST but ranges use STI)
  const scorecardKey = indicatorId === 'ST1' ? 'STI1' : indicatorId === 'ST2' ? 'STI2' : indicatorId;
  const ranges = (scorecardRanges as any)[scorecardKey];

  if (!ranges || ranges.length === 0) {
    console.warn(`No scorecard ranges found for indicator: ${indicatorId} (lookup key: ${scorecardKey})`);
    return null;
  }

  // Find the applicable range
  for (const range of ranges) {
    const { starting_point, ending_point, asociado_1, asociado_2, puntaje } = range;

    // Check if value falls in this range
    const inRange = (
      (starting_point === null || value >= starting_point) &&
      (ending_point === null || value < ending_point)
    );

    if (inRange) {
      // If puntaje is set, return it directly (fixed score for edge cases)
      if (puntaje !== null && puntaje !== 0) {
        return puntaje;
      }

      // Apply interpolation formula
      // score = asociado_1 + ((value - inferior) / (superior - inferior)) * (asociado_2 - asociado_1)
      if (asociado_1 !== null && asociado_2 !== null && starting_point !== null && ending_point !== null) {
        const score = asociado_1 +
          ((value - starting_point) / (ending_point - starting_point)) *
          (asociado_2 - asociado_1);

        return Math.min(100, Math.max(0, score));
      }

      // Fallback for edge ranges
      if (puntaje !== null) {
        return puntaje;
      }
    }
  }

  // Value doesn't fall in any range - return 0 or 100 depending on direction
  const direction = INDICATOR_DIRECTIONALITY[scorecardKey]; // Use scorecardKey for consistency
  const firstRange = ranges[0];
  const lastRange = ranges[ranges.length - 1];

  // If value is below all ranges
  if (firstRange.starting_point !== null && value < firstRange.starting_point) {
    return direction === 'higher' ? 0 : 100;
  }

  // If value is above all ranges
  if (lastRange.ending_point !== null && value >= lastRange.ending_point) {
    return direction === 'higher' ? 100 : 0;
  }

  console.warn(`Could not find applicable range for ${indicatorId} with value ${value}`);
  return null;
};

/**
 * Indicator weights within each component (from camel_weights.yaml)
 */
const INDICATOR_WEIGHTS: Record<string, Record<string, number>> = {
  C: {
    C1: 0.338531955,
    C2: 0.141941911,
    C3: 0.265623524,
    C4: 0.253902609,
  },
  EG: {
    EG1: 0.454951896,
    EG2: 0.196039854,
    EG3: 0.34900825,
  },
  L: {
    L1: 0.302018768,
    L2: 0.311137119,
    L3: 0.386844114,
  },
  R: {
    R1: 0.068846098,
    R2: 0.134972783,
    R3: 0.494324805,
    R4: 0.041140737,
    R5: 0.033740116,
    R6: 0.226975462,
  },
  S: {
    S1: 0.094015723,
    S2: 0.05516997,
    S3: 0.000740908,
    S4: 0.098460019,
    S5: 0.329294498,
    S6: 0.412384709,
    S7: 0.009934172,
  },
  STI: {
    STI1: 0.5,
    STI2: 0.5,
    ST1: 0.5,  // Alias for STI1 (data uses ST1)
    ST2: 0.5,  // Alias for STI2 (data uses ST2)
  },
};

/**
 * Indicator to component mapping
 */
export const INDICATOR_TO_COMPONENT: Record<string, string> = {
  C1: 'C', C2: 'C', C3: 'C', C4: 'C',
  EG1: 'EG', EG2: 'EG', EG3: 'EG',
  L1: 'L', L2: 'L', L3: 'L', L4: 'L',
  R1: 'R', R2: 'R', R3: 'R', R4: 'R', R5: 'R', R6: 'R',
  S1: 'S', S2: 'S', S3: 'S', S4: 'S', S5: 'S', S6: 'S', S7: 'S',
  STI1: 'STI', STI2: 'STI',
  ST1: 'STI', ST2: 'STI',  // Data uses ST1/ST2 instead of STI1/STI2
};

/**
 * Recalculate component scores from raw indicator data using official scorecard ranges
 *
 * @param indicators - Array of indicators with indicator_id and indicator_value
 * @returns Array of component scores { code, score }
 */
export const recalculateComponentScores = (indicators: Array<{ indicator_id: string; indicator_value: number }>) => {
  const componentScores: Record<string, { totalWeightedScore: number; totalWeight: number }> = {
    C: { totalWeightedScore: 0, totalWeight: 0 },
    EG: { totalWeightedScore: 0, totalWeight: 0 },
    L: { totalWeightedScore: 0, totalWeight: 0 },
    R: { totalWeightedScore: 0, totalWeight: 0 },
    S: { totalWeightedScore: 0, totalWeight: 0 },
    STI: { totalWeightedScore: 0, totalWeight: 0 },
  };

  // Process each indicator
  indicators.forEach(indicator => {
    const indicatorId = indicator.indicator_id;
    const componentCode = INDICATOR_TO_COMPONENT[indicatorId];

    if (!componentCode) {
      // Not a CAMEL indicator, skip
      return;
    }

    // Get the indicator's scorecard score (0-100)
    const indicatorScore = calculateIndicatorScore(indicatorId, indicator.indicator_value);

    if (indicatorScore === null) {
      console.warn(`Could not calculate score for ${indicatorId}`);
      return;
    }

    // Get the indicator's weight within its component
    const weight = INDICATOR_WEIGHTS[componentCode]?.[indicatorId];

    if (weight === undefined) {
      console.warn(`No weight found for ${indicatorId} in component ${componentCode}`);
      return;
    }

    // Accumulate weighted score
    componentScores[componentCode].totalWeightedScore += indicatorScore * weight;
    componentScores[componentCode].totalWeight += weight;
  });

  // Calculate final component scores
  return Object.entries(componentScores).map(([code, data]) => ({
    code,
    score: data.totalWeight > 0 ? data.totalWeightedScore / data.totalWeight : 0,
  }));
};
